import {Injectable, OnDestroy} from '@angular/core';
import {GlobalService} from '../../global/global.service';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {UserWsInfo} from '../model/user-ws-info';
import {AuthStorage} from '../../auth/auth-storage';
import {LatestVehicleEntry} from '../../component/latest-vehicles-info/model/latest-vehicle-entry';
import {LatestVehiclesCacheEntry} from './model/latest-vehicles-cache-entry';
import * as Pako from 'pako'
import {VehiclesService} from '../../../pages/client/vehicles/vehicles.service';
import {takeUntil, timeout} from 'rxjs/operators';
import {SubscriptionUtils} from "../../commons/subscription.utils";
import {ReconnectingSocket} from '../reconnecting-socket';

const LATEST_VEHICLES_TOPIC = `/users/assets-state/latest-vehicles`;
const INITIAL_SESSION_ACTIVATION_ENDPOINT = `/app/initial-session-activation`;
const ACTIVATE_SESSION_ENDPOINT = `/app/activate-session`;
const DEACTIVATE_SESSION_ENDPOINT = `/app/deactivate-session`;
const UPDATE_SELECTION_ENDPOINT = `/app/update/selection`;
const UPDATE_GROUP_SUBS_ENDPOINT = `/app/update/group-subs`;
const REFRESH_ENDPOINT = `/app/refresh`;

const RECONNECT_TIMEOUT = 20 * 1000;
const STALE_CACHE_REFRESH_IN_SECONDS = 20 * 1000;
const DEAD_SOCKET_CACHE_AGE = 45 * 1000;
const NOT_VISIBLE_VEHICLES_TIMEOUT = 8 * 1000;

const fallbackTransports = [
  // 'websocket',
  'xhr-streaming',
  'xdr-streaming',
  'eventsource',
  'iframe-eventsource',
  'htmlfile',
  'iframe-htmlfile',
  'xhr-polling',
  'xdr-polling',
  'iframe-xhr-polling',
  'jsonp-polling'
];

@Injectable({
  providedIn: 'root'
})
export class AssetsStateWebSocketService implements OnDestroy {

  // ---------------------------------
  public ASWSenabled = true; // <---------------------------- note enable assets state latest vehicles / tails
  // ---------------------------------

  private socket: ReconnectingSocket;

  private collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});

  private deactivateSessionSource = new Subject<void>();
  public deactivateSession$ = this.deactivateSessionSource.asObservable();

  private activateSessionSource = new Subject<void>();
  public activateSession$ = this.activateSessionSource.asObservable();

  private latestVehiclesSource = new BehaviorSubject<LatestVehicleEntry[]>(this.loadFromAuthStorage());
  public latestVehicles$ = this.latestVehiclesSource.asObservable();

  private deactivateSessionSubs: Subscription;
  private activateSessionSubs: Subscription;

  private showTrailers: Boolean = false;

  private timeOfCache;
  private isSessionActive = false;
  private subSubject = new Subject<boolean>();

  private notVisibleVehicles: number[] = [];
  private notVisibleVehiclesSub: Subscription;

  constructor(private globalService: GlobalService,
              private vehiclesService: VehiclesService) {

    this.socket = new ReconnectingSocket({
      hiddenReconnectDelay: RECONNECT_TIMEOUT,
      canConnect: () => this.ASWSenabled,
      fetchUserInfo: () => this.globalService.userAssetsStateWsInfo(),
      beforeConnect: () => this.loadNotVisibleVehicles(),
      headers: (userInfo: UserWsInfo) => this.buildHeaders(userInfo),
      onConnected: () => this.onConnected(),
      onResume: () => this.refreshIfCacheStale(),
      isDataFlowing: () => !this.isSessionActive || !this.isCacheExpired(DEAD_SOCKET_CACHE_AGE)
    });

    this.latestVehicles$.pipe(takeUntil(this.subSubject)).subscribe(v => {
      this.saveToAuthStorage(v);
    });
  }

  public getUserAndConnect(): void {
    this.socket.connect();
  }

  public disconnect(): void {
    this.socket.disconnect();
    this.resetLocalCache();
    this.unsubAll();
  }

  public initSessionsSubs(): void {
    this.unsub(this.activateSessionSubs)
    this.activateSessionSubs = this.activateSession$.subscribe(
      () => this.sendActivateSession()
    );
    this.unsub(this.deactivateSessionSubs)
    this.deactivateSessionSubs = this.deactivateSession$.subscribe(
      () => this.sendDeactivateSession()
    );
  }

  public deactivateSession(): void {
    this.deactivateSessionSource.next();
  }

  public activateSession(): void {
    this.activateSessionSource.next();
  }

  public sendDeactivateSession(): void {
    if (this.socket.isConnected()) {
      this.socket.send(DEACTIVATE_SESSION_ENDPOINT);
      this.isSessionActive = false;
    }
  }

  public sendActivateSession(): void {
    if (this.socket.isConnected()) {
      this.socket.send(ACTIVATE_SESSION_ENDPOINT, JSON.stringify(this.getClientSelection()));
      this.isSessionActive = true;
      this.refreshIfCacheStale();
    }
  }

  public toggleShowTrailers(showTrailers: Boolean): void {
    this.showTrailers = showTrailers;
    this.sendUpdateClientSelection();
  }

  public sendUpdateClientSelection(): void {
    this.socket.send(UPDATE_SELECTION_ENDPOINT, JSON.stringify(this.getClientSelection()));
  }

  public sendUpdateSubedGroups(): void {
    this.socket.send(UPDATE_GROUP_SUBS_ENDPOINT);
  }

  public resetLocalCache(): void {
    this.timeOfCache = moment().subtract(1, 'minutes');
    this.latestVehiclesSource.next([]);
    this.showTrailers = false;
  }

  public ngOnDestroy(): void {
    this.unsubAll();
    this.socket.destroy();
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
  }

  public initialSessionActivation(): void {
    this.socket.send(INITIAL_SESSION_ACTIVATION_ENDPOINT, JSON.stringify(this.getClientSelection()));
    this.isSessionActive = true;
  }

  private onConnected(): void {
    this.initSessionsSubs();
    this.initialSessionActivation();
    this.socket.subscribe(LATEST_VEHICLES_TOPIC, (event) => {
      try {
        this.decompressAndHandle(event);
      } catch (e) {
        this.handleLatestVehicles(JSON.parse(event.body));
      }
    });
  }

  private buildHeaders(userInfo: UserWsInfo): object {
    AuthStorage.persistClientId(userInfo.clientId);
    AuthStorage.persistZoneId(userInfo.zoneId);
    return {
      employeeId: userInfo.employeeId,
      clientId: userInfo.clientId,
      token: userInfo.token,
      accountId: userInfo.accountId,
      partner: userInfo.partner
    };
  }

  private loadNotVisibleVehicles(): void {
    this.unsub(this.notVisibleVehiclesSub);
    this.notVisibleVehiclesSub = this.vehiclesService.getNotVisibleVehicles()
      .pipe(timeout(NOT_VISIBLE_VEHICLES_TIMEOUT))
      .subscribe({
        next: notVisibleVehicles => this.notVisibleVehicles = notVisibleVehicles,
        error: () => undefined
      });
  }

  private loadFromAuthStorage(): LatestVehicleEntry[] {
    // return AuthStorage.getLatestVehicles().map(v => new LatestVehicleEntry(v));
    return [];
  }

  private saveToAuthStorage(value: LatestVehicleEntry[]) {
    // AuthStorage.saveLatestVehicles(value);
  }

  private decompressAndHandle(event) {
    const strData = atob(event.body);
    const charData = strData.split('').map((it) => it.charCodeAt(0));
    const binData = new Uint8Array(charData);
    const result = Pako.inflate(binData, { to: 'string' });
    this.handleLatestVehicles(JSON.parse(result));
  }

  private handleLatestVehicles = (vehiclesCache: LatestVehiclesCacheEntry) => {
    if (this.isMessageValid(vehiclesCache)) {
      this.handleValidMessage(vehiclesCache);
    }
  };

  private handleValidMessage(vehiclesCache: LatestVehiclesCacheEntry) {
    this.timeOfCache = moment.utc(vehiclesCache.timeOfCache);

    const visibleForUserVehicles = this.filterUserNotVisibleVehicles(vehiclesCache.latestVehicles);
    const sortedLatestVehicles = visibleForUserVehicles
      .map(entry => new LatestVehicleEntry(entry))
      .sort((v1, v2) => this.collator.compare(v1.plateNumber, v2.plateNumber));
    this.latestVehiclesSource.next(sortedLatestVehicles);
  }

  private filterUserNotVisibleVehicles(latestVehicles: LatestVehicleEntry[]): LatestVehicleEntry[] {
    return latestVehicles.filter(it => !this.notVisibleVehicles.includes(it.id));
  }

  private isMessageValid(vehiclesCache: LatestVehiclesCacheEntry): boolean {
    const length = vehiclesCache.latestVehicles.length;
    return length !== 0;
  }

  private refreshIfCacheStale(): void {
    const isCacheStale = this.isCacheExpired(STALE_CACHE_REFRESH_IN_SECONDS);
    if (isCacheStale) {
      this.socket.send(REFRESH_ENDPOINT);
    }
  }

  private isCacheExpired(timeForExpiration: number) {
    const timeAfterLastCache = moment() - this.timeOfCache;
    return timeAfterLastCache > timeForExpiration;
  }

  private getClientSelection() {
    return {
      selectedGroups: this.getUserSelectedGroups(),
      showTrailers: this.showTrailers
    };
  }

  private getUserSelectedGroups(): number[] {
    return AuthStorage.getUserVehicleGroups().length > 0
      ? [...AuthStorage.getUserVehicleGroups().map(it => it.id)]
      : AuthStorage.isLoggedPartnerEmployee()
        ? []
        : [AuthStorage.getUserGroup().id];
  }

  private unsubAll(): void {
    this.unsub(this.deactivateSessionSubs);
    this.unsub(this.activateSessionSubs);
  }

  private unsub(subscription: Subscription): void {
    if (subscription) {
      subscription.unsubscribe();
    }
  }

}
